import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import MarkdownRenderer from '../common/MarkdownRenderer';
import MermaidPreview from '../mermaid/MermaidPreview';
import useNotification from '../../hooks/useNotification';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import { Accordion, ScrollableBox, EditableTextField } from '../atoms';
import LoadMoreButton from '../atoms/LoadMore';
import EmptyStateBox from '../atoms/EmptyStateBox';
import useSectionCRUD from '../../hooks/useSectionCRUD';
import useContentActions from '../../hooks/useContentActions';
import useExplain from '../../hooks/useExplain';
import useUserPermissionsVersions from '../../hooks/useUserPermissionsVersions';
import VersionSelector from '../atoms/VersionSelector';
import ExplanationModal from '../atoms/ExplanationModal';
import ImprovementModal from '../atoms/ImprovementModal';
import useFullScreen from '../../hooks/useFullScreen';
import TopBarButtons from '../atoms/TopBarButtons';
import ConfirmationDialog from '../common/ConfirmationDialog';
import SectionHeader from '../atoms/Header';
import LoadingBox from '../atoms/LoadingBox';
import { capitalizeFirst } from '../../utils/textTransformations';
import conversationService from '../../services/conversationService';
import AddSectionModal from '../atoms/AddSectionModal';
import useImprove from '../../hooks/useImprove';
import InlineImproveInput from '../common/InlineImproveInput';
import useParsedSections from '../../hooks/useParsedSections';

const SplitViewUserPermissions = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1); // Default zoom to 1x for diagram visibility
  const { showNotification } = useNotification();
  const sectionPanelRef = useRef(null);

  // Helper function to extract Mermaid code from content
  const extractMermaidCode = (content) => {
    if (!content) return null;
    const mermaidMatch = content.match(/```mermaid\s*([\s\S]*?)```/);
    return mermaidMatch ? mermaidMatch[1].trim() : null;
  };

  // Helper function to check if content has Mermaid diagram
  const hasMermaidDiagram = (content) => {
    return content && content.includes('```mermaid');
  };

  // Zoom controls for Mermaid diagram - matching Architecture implementation
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 2.0));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.25));
  };

  const isLocalUpdateRef = useRef(false); // Track if we're doing a local update

  // Use version management for user permissions - pass isLocalUpdateRef
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useUserPermissionsVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedSections,
    setSections: setParsedSections,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef: sectionContentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // State for managing expanded/visible sections
  const [expandedSections, setExpandedSections] = useState({});

  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedSections, setExpandedSections);
  const [visibleSections, setVisibleSections] = useState(10);

  // Track previous version index to detect actual version changes
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Version switching: reset visible sections when switching versions
  useEffect(() => {
    if (currentVersion) {
      if (prevVersionIndexRef.current !== currentVersionIndex) {
        setVisibleSections(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  const showSectionPanel = parsedSections.length > 0;

  const handleSectionToggle = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleLoadMore = () => {
    setVisibleSections(prev => Math.min(prev + 10, parsedSections.length));
  };

  const handleShowAll = () => {
    setVisibleSections(parsedSections.length);
  };

  // Use CRUD operations hook
  const {
    editingSection,
    editedContent,
    editedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    handleStartEdit: handleStartEditOriginal,
    handleCancelEdit,
    handleSaveEdit: handleSaveEditOriginal,
    handleDeleteSection: handleDeleteSectionClick,
    handleDeleteSectionConfirmed: handleDeleteConfirmGeneric,
    handleCopySection: handleCopySectionOriginal
  } = useSectionCRUD(conversation, parsedSections, setParsedSections, setExpandedSections, sectionContentRef, updateCurrentVersion, currentVersion, isLocalUpdateRef, onConversationUpdate,
    markLocalEdit
  );

  // Custom delete handler
  const handleDeleteConfirm = async () => {
    const section = deleteConfirmation.section;
    setDeleteConfirmation({ open: false, section: null });

    if (!section) return;

    try {
      // Remove the section from the sections array
      const updatedSections = parsedSections.filter(s => s.id !== section.id);

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedSections(updatedSections);

      // Remove expanded state for this section
      setExpandedSections(prev => {
        const updated = { ...prev };
        delete updated[section.id];
        return updated;
      });

      // Reconstruct the full content with proper format
      let updatedContent = '';
      updatedSections.forEach((sec, idx) => {
        if (idx > 0) updatedContent += '\n\n';
        updatedContent += `## ${sec.title}\n\n${sec.content}`;
      });

      // Update content ref
      sectionContentRef.current = updatedContent.trim();

      // Update the current version with new content
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          sections: updatedSections
        });
      }

      // Save to backend - update the specific message for the current version
      const updatedMessages = [...conversation.messages];

      // Find the message ID for the current version
      const currentVersionMessageId = currentVersion?.id;
      let messageUpdated = false;

      // Try to find and update the specific message by ID
      if (currentVersionMessageId) {
        for (let i = 0; i < updatedMessages.length; i++) {
          if (updatedMessages[i]._id === currentVersionMessageId ||
              updatedMessages[i].id === currentVersionMessageId) {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            messageUpdated = true;
            break;
          }
        }
      }

      // Fallback: update the last assistant message if we couldn't find by ID
      if (!messageUpdated) {
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Notify parent component to refresh conversation
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      showNotification({
        message: `"${section.title}" deleted successfully`,
        type: 'success'
      });

    } catch (error) {
      console.error('Error deleting section:', error);
      showNotification({
        message: `Failed to delete "${section.title}"`,
        type: 'error'
      });
    }
  };

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Use improve hook
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    registerSectionRef,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Use content actions hook
  const {
    isExporting,
    isSaving: isSavingDocument,
    handleExport: handleExportSections,
    getExportContent,
    handleCopy: handleCopySections,
    handleRefresh: handleRefreshSections
  } = useContentActions(sectionContentRef, 'User Permissions', projectId, conversation?._id);

  // Add full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Initialize validation state for sections with version-specific IDs
  const initialValidationState = parsedSections.reduce((acc, section) => {
    // Create version-specific validation ID
    const versionSpecificId = `${section.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  // Use the validation hook with version-specific IDs
  const {
    validatedItems: validatedSections,
    handleValidateItem: handleValidateSectionBase,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'user-permissions',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'user-permissions',
  });

  // Wrapper function to handle version-specific validation
  const handleValidateSection = async (sectionId) => {
    // Create version-specific ID
    const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateSectionBase(versionSpecificId);
  };

  // Create wrapper functions to maintain compatibility
  const handleStartEdit = (sectionId, section, title) => {
    // Extract content for editing
    let contentToEdit = section.rawContent || section.content || '';
    handleStartEditOriginal(sectionId, contentToEdit, title);
  };

  const handleSaveEdit = (sectionId) => {
    return handleSaveEditOriginal(sectionId);
  };

  const handleCopySection = (section) => {
    return handleCopySectionOriginal(section);
  };

  const handleExplainSection = (section) => {
    requestExplanation(section);
  };

  const handleImproveSection = (section) => {
    handleImproveClick(section);
  };

  // Handle update from improvement modal
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;

    try {
      // Handle remove action — delete section from artifact
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        // Trigger a conversation refresh to pick up the change
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }


      // Use merged content if it's a specific section improvement
      const contentToUse = improveSection.mergedContent || improvedContent;

      // Update the section with improved content
      const updatedSections = [...parsedSections].map(s => {
        if (s.id === improveSection.id) {
          return {
            ...s,
            content: contentToUse,
            rawContent: contentToUse
          };
        }
        return s;
      });

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedSections(updatedSections);

      // Reconstruct the full content with updated sections
      let updatedContent = '';
      updatedSections.forEach((sec, idx) => {
        if (idx > 0) updatedContent += '\n\n';
        updatedContent += `## ${sec.title}\n\n${sec.content}`;
      });

      sectionContentRef.current = updatedContent.trim();

      // Update the current version with new sections using the hook's update function
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          sections: updatedSections,
          content: updatedContent.trim(),
          parsedContent: updatedSections
        });
      }

      // Save to backend
      const updatedMessages = [...conversation.messages];

      // Find the message ID for the current version
      const currentVersionMessageId = currentVersion?.id;
      let messageUpdated = false;

      // Try to find and update the specific message by ID
      if (currentVersionMessageId) {
        for (let i = 0; i < updatedMessages.length; i++) {
          if (updatedMessages[i]._id === currentVersionMessageId ||
              updatedMessages[i].id === currentVersionMessageId) {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            messageUpdated = true;
            break;
          }
        }
      }

      // Fallback: update the last assistant message if we couldn't find by ID
      if (!messageUpdated) {
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Notify parent component to refresh conversation
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      closeImprovementModal();

      showNotification({
        message: 'Section improved successfully',
        type: 'success'
      });

    } catch (error) {
      showNotification({
        message: 'Failed to update improved content',
        type: 'error'
      });
    }
  };

  // Handler for adding new section
  const handleAddNewSection = async (newSectionData) => {
    try {
      // Only add to the latest version
      if (!isLatestVersion) {
        showNotification({
          message: 'Please switch to the latest version to add new sections',
          type: 'warning'
        });
        setShowAddSectionModal(false);
        return;
      }

      // Create the new section
      const displaySection = {
        id: newSectionData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        title: newSectionData.title,
        content: newSectionData.content,
        rawContent: newSectionData.content
      };

      // Add the new section to the current sections
      const updatedSections = [...parsedSections, displaySection];

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedSections(updatedSections);

      // Expand the new section by default
      setExpandedSections(prev => ({ ...prev, [displaySection.id]: true }));

      // Ensure the new section is visible if we're using pagination
      if (visibleSections < updatedSections.length) {
        setVisibleSections(updatedSections.length);
      }

      // Update conversation messages with new section
      const sectionText = `## ${displaySection.title}\n\n${displaySection.content}`;
      const updatedContent = sectionContentRef.current + '\n\n' + sectionText;

      // Update content ref
      sectionContentRef.current = updatedContent;

      // Save to backend
      const updatedMessages = [...conversation.messages];
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (updatedMessages[i].role === 'assistant') {
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: updatedContent.trim()
          };
          break;
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      showNotification({
        message: 'Section added successfully',
        type: 'success'
      });

      // Close modal after successful save
      setShowAddSectionModal(false);

      // Update the current version with the new sections
      if (updateCurrentVersion && currentVersion) {
        const updatedVersion = {
          ...currentVersion,
          sections: updatedSections,
          content: updatedContent.trim(),
          parsedContent: updatedSections
        };
        updateCurrentVersion(updatedVersion);
      }

      // Update the conversation in the parent component to reflect changes in left panel
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      // Scroll to the new section
      setTimeout(() => {
        if (sectionPanelRef.current) {
          sectionPanelRef.current.scrollTop = sectionPanelRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      showNotification({
        message: 'Failed to add new section',
        type: 'error'
      });
    }
  };

  // Auto-scroll section panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && sectionPanelRef.current) {
      // Wait a bit to ensure content renders before scrolling
      setTimeout(() => {
        if (sectionPanelRef.current) {
          sectionPanelRef.current.scrollTop = sectionPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming]);

  // Don't show the split view if no sections are found and we're not streaming
  if (!showSectionPanel && !isStreaming) {
    return <>{children}</>;
  }

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      mt: 5,
    }}>

      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(false),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Section accordion panel */}
        <Box
          ref={sectionPanelRef}
          sx={{
            ...getRightPanelStyles(false),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <SectionHeader label='User Permissions' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={(newIndex) => {
                      // Reset local update flag when user explicitly switches versions
                      isLocalUpdateRef.current = false;
                      setCurrentVersionIndex(newIndex);
                    }}
                    disabled={isStreaming}
                  />
                )}
                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  onAdd={isLatestVersion ? () => setShowAddSectionModal(true) : null}
                  getExportContent={getExportContent}
                  onExport={handleExportSections}
                  isExporting={isExporting}
                  onCopy={handleCopySections}
                  onRefresh={handleRefreshSections}
                  disabled={!sectionContentRef.current}
                  addTooltip={!isLatestVersion ? "Switch to latest version to add sections" : "Add new section"}
                  exportTooltip="Export user permissions as markdown"
                  copyTooltip="Copy user permissions to clipboard"
                  refreshTooltip="Refresh user permissions"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'user-permissions'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedSections}
              totalItems={parsedSections?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            {/* Show parsed sections or a placeholder if none found yet */}
            {/* Loading while generating */}
            {isStreaming ? (
              <LoadingBox message='Generating user permissions...' spinnerSize={40} />
            ) : showSectionPanel && parsedSections.length > 0 ? (
              <Box sx={{
                p: 3,
                pt: 2,
                flex: 1,
                overflow: "auto",
                '&::-webkit-scrollbar': {
                  width: '8px',
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-track': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  marginTop: '5px',
                  marginBottom: '5px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.4)',
                  },
                },
                '&::-webkit-scrollbar-corner': {
                  backgroundColor: 'transparent',
                },
              }}>
                {/* Render sections as accordions grouped by category */}
                {(() => {
                  const groups = groupedSections;
                  let globalIndex = 0;
                  return groups.slice(0, visibleSections).map((group, groupIndex) => (
                    <Box key={group.categoryId} sx={{ mb: 3 }}>
                      {group.category && (
                        <Box sx={{ mt: groupIndex === 0 ? 0 : 2, mb: 1.5 }}>
                          <SectionHeader label={group.category} />
                        </Box>
                      )}

                      {group.categorySection && (
                        <Box sx={{ pl: 2, mb: 2 }}>
                          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                            <MarkdownRenderer content={group.categorySection.content || ''} />
                          </Box>
                        </Box>
                      )}

                      <Box sx={{ pl: group.category ? 2 : 0 }}>
                        {group.sections.map((section) => {
                          const idx = globalIndex++;
                          const sectionId = section.id;
                          const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                          const isValidated = validatedSections[versionSpecificId] || false;

                          return (
                            <Accordion
                              key={sectionId}
                              ref={(el) => { registerSectionRef(sectionId, el); if (shouldHighlight(sectionId)) highlightRef.current = el; }}
                              title={capitalizeFirst(section.title)}
                              defaultExpanded={false}
                              expanded={expandedSections[sectionId] === true}
                              onChange={(_, expanded) => handleSectionToggle(sectionId)}
                              titleContent={editingSection === sectionId ? (
                                <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                                  <TextField
                                    value={editedTitle}
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.target.removeAttribute('title')}
                                    size="small"
                                    fullWidth
                                    placeholder="Enter permission title"
                                    variant="outlined"
                                    InputProps={{ title: '' }}
                                    inputProps={{ title: '', autoComplete: 'off' }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        backgroundColor: '#fff',
                                        '& fieldset': { borderWidth: '1px' },
                                        '&:hover fieldset': { borderWidth: '1px' },
                                        '&.Mui-focused fieldset': { borderWidth: '1px' },
                                      },
                                      '& .MuiOutlinedInput-input': {
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        padding: '8px 12px',
                                      },
                                    }}
                                  />
                                </Box>
                              ) : null}
                              rightIcon={
                                <Box
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    try {
                                      await handleValidateSection(sectionId);
                                    } catch (error) {
                                      showNotification({
                                        message: `Failed to update validation for "${section.title}"`,
                                        type: "error"
                                      });
                                    }
                                  }}
                                  sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
                                >
                                  {isValidated ? (
                                    <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} />
                                  ) : (
                                    <UnvalidatedIcon size={25} />
                                  )}
                                </Box>
                              }
                            >
                              <ScrollableBox
                                sx={{
                                  textAlign: 'justify',
                                  cursor: editingSection === sectionId ? 'default' : 'pointer',
                                }}
                              >
                                {editingSection === sectionId ? (
                                  <EditableTextField
                                    value={editedContent}
                                    onChange={(e) => setEditedContent(e.target.value)}
                                  />
                                ) : (
                                  <Box ref={getSectionContentRef(sectionId)}>
                                    <TextSelectionPopup
                                      containerRef={getSectionContentRef(sectionId)}
                                      sectionId={sectionId}
                                      onSubmitComment={addComment}
                                      disabled={editingSection === sectionId}
                                    />
                                    {hasMermaidDiagram(section.content || section.rawContent || '') ? (
                                      <Box>
                                        {expandedSections[sectionId] === true && (
                                          <MermaidPreview
                                            key={`permission-diagram-${sectionId}`}
                                            code={extractMermaidCode(section.content || section.rawContent || '')}
                                            zoomLevel={zoomLevel}
                                            id={`permission-diagram-${sectionId}`}
                                            minHeight={300}
                                            maxHeight={600}
                                          />
                                        )}

                                        {(() => {
                                          const content = section.content || section.rawContent || '';
                                          const textBeforeMermaid = content.split('```mermaid')[0].trim();
                                          const textAfterMermaid = content.split('```')[content.split('```').length - 1].trim();
                                          return (
                                            <>
                                              {textBeforeMermaid && (
                                                <Box sx={{ mb: 2 }}>
                                                  <MarkdownRenderer content={textBeforeMermaid} />
                                                </Box>
                                              )}
                                              {textAfterMermaid && (
                                                <Box sx={{ mt: 2 }}>
                                                  <MarkdownRenderer content={textAfterMermaid} />
                                                </Box>
                                              )}
                                            </>
                                          );
                                        })()}
                                      </Box>
                                    ) : (
                                      <MarkdownRenderer content={section.content || section.rawContent || ''} />
                                    )}

                                    {showImproveInput === sectionId && (
                                      <InlineImproveInput
                                        improvePrompt={improvePrompt}
                                        onPromptChange={setImprovePrompt}
                                        onSend={() => handleSendImprove(section)}
                                        onCancel={handleCancelImprove}
                                        promptText="How would you like to improve this section?"
                                        placeholder="Describe the improvements you'd like..."
                                      />
                                    )}
                                  </Box>
                                )}
                              </ScrollableBox>

                              <Box sx={{
                                position: 'absolute',
                                right: 10,
                                top: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5
                              }}>
                                <ActionButtons
                                  item={section}
                                  itemIndex={idx}
                                  featureId={sectionId}
                                  editingFeature={editingSection}
                                  isSaving={isSaving}
                                  onEdit={() => handleStartEdit(sectionId, section, section.title || '')}
                                  onSave={() => handleSaveEdit(sectionId)}
                                  onCancel={handleCancelEdit}
                                  onImprove={() => handleImproveSection(section)}
                                  onExplain={() => handleExplainSection(section)}
                                  onCopy={() => handleCopySection(section)}
                                  onDelete={() => handleDeleteSectionClick(section)}
                                  vertical={true}
                          onComment={() => openCommentPanel(sectionId)}
                          commentCount={commentCounts[sectionId]?.open || 0}
                          isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(versionSpecificId) : null}
                                  showZoomControls={hasMermaidDiagram(section.content || section.rawContent || '')}
                                  onZoomIn={handleZoomIn}
                                  onZoomOut={handleZoomOut}
                                  zoomLevel={zoomLevel}
                                  minZoom={0.25}
                                  maxZoom={2.0}
                                />
                              </Box>
                            </Accordion>
                          );
                        })}
                      </Box>
                    </Box>
                  ));
                })()}

                {/* Load more button for lazy loading */}
                {(() => {
                  const groups = groupedSections;
                  return groups.length > visibleSections && (
                    <LoadMoreButton
                      remaining={groups.length - visibleSections}
                      onLoadMore={handleLoadMore}
                      onShowAll={handleShowAll}
                      label="Load more"
                    />
                  );
                })()}

              </Box>
            ) : (
              <EmptyStateBox message="No user permissions found in this conversation yet." />
            )}
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        title="Delete section"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height="auto"
        onConfirm={handleDeleteConfirm}
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Add Section Modal */}
      <AddSectionModal
        open={showAddSectionModal}
        onClose={() => setShowAddSectionModal(false)}
        onSave={handleAddNewSection}
        sectionType="permission"
        existingSections={parsedSections}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        onUpdate={handleUpdateImprovedContent}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />

    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedSections || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewUserPermissions;
